/**
 * LabsPage · /labs · SFUI-003 Museum Standard
 * Architect Directive 2026-07-05 · NextXus Core Labs
 *
 * Free AI tools for all Federation citizens, powered by the DeepAI
 * proxy on the backend (backend/routers/labs.py). Two tools:
 *   1. AI Chat        · POST /api/labs/chat            (text → text)
 *   2. Voice Chat     · POST /api/labs/speech-to-text  (audio → text)
 *                       POST /api/labs/chat            (text  → text)
 *                       POST /api/labs/tts             (text  → audio url)
 */
import React, { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "chat",  label: "AI Chat",     icon: "💬", hint: "Type-in conversation" },
  { id: "voice", label: "Voice Chat",  icon: "🎙️", hint: "Speak-in / hear-back" },
];

// ─────────────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────────────
export default function LabsPage() {
  const [tab, setTab] = useState("chat");

  return (
    <main
      data-testid="labs-page"
      className="min-h-screen relative z-10 px-4 sm:px-6 lg:px-10 py-12"
      style={{ color: "#e6fbff" }}
    >
      <div className="max-w-[1080px] mx-auto">
        <Header />

        <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Labs tools">
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                data-testid={`labs-tab-${t.id}`}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className="font-mono uppercase tracking-[0.22em] transition-all"
                style={{
                  padding:      "10px 18px",
                  fontSize:     11,
                  color:        active ? "#0b0d13" : "#7fe7f5",
                  background:   active
                    ? "linear-gradient(135deg, #7fe7f5 0%, #06b6d4 100%)"
                    : "rgba(4,5,10,0.55)",
                  border:       `1px solid rgba(6,182,212,${active ? 0.85 : 0.32})`,
                  borderRadius: 8,
                  cursor:       "pointer",
                  fontWeight:   active ? 700 : 500,
                  boxShadow:    active ? "0 0 18px rgba(6,182,212,0.44), inset 0 1px 0 rgba(255,255,255,0.3)" : "none",
                }}
              >
                {t.icon} {t.label}
              </button>
            );
          })}
          <div className="ml-auto flex items-center">
            <span
              data-testid="labs-free-badge"
              className="font-mono uppercase tracking-[0.24em]"
              style={{
                padding:      "8px 14px",
                fontSize:     10,
                color:        "#ffd24a",
                background:   "rgba(255,189,0,0.08)",
                border:       "1px solid rgba(255,210,74,0.55)",
                borderRadius: 999,
                boxShadow:    "0 0 14px rgba(255,189,0,0.18)",
              }}
            >
              ◆ Free for all NextXus Citizens
            </span>
          </div>
        </div>

        {tab === "chat"  && <AIChatConsole />}
        {tab === "voice" && <VoiceConsole />}

        <Footer />
      </div>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────
function Header() {
  return (
    <header className="mb-8">
      <div className="flex items-baseline gap-3 mb-4">
        <span aria-hidden="true" style={{ width: 10, height: 10, background: "#06b6d4", boxShadow: "0 0 12px #06b6d4" }} />
        <span className="font-mono uppercase tracking-[0.28em]" style={{ fontSize: 11, color: "#7fe7f5" }}>
          NextXus Core · Command Labs · SFUI-003
        </span>
        <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, rgba(6,182,212,0.4) 0%, transparent 100%)" }} />
      </div>
      <h1
        data-testid="labs-title"
        spellCheck={false}
        translate="no"
        className="font-display"
        style={{
          fontSize:      "clamp(34px, 5vw, 54px)",
          lineHeight:    1.05,
          color:         "#e6fbff",
          textShadow:    "0 0 22px rgba(6,182,212,0.35)",
          letterSpacing: "-0.01em",
        }}
      >
        NextXus Core Labs
      </h1>
      <p
        data-testid="labs-subtitle"
        className="mt-3 font-serif"
        style={{
          fontSize:  "clamp(15px, 1.4vw, 18px)",
          color:     "rgba(230,251,255,0.72)",
          maxWidth:  720,
          lineHeight: 1.55,
        }}
      >
        Powered by NextXus — <em>Express Your Voice</em>. Free conversational tools for every Federation citizen: type or speak, and let the Federation reply.
      </p>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tool 1 · AI Chat Console
// ─────────────────────────────────────────────────────────────────────
function AIChatConsole() {
  const [messages, setMessages] = useState([
    { role: "system", text: "Command Labs online. Ask anything — replies flow through the NextXus proxy." },
  ]);
  const [input, setInput]   = useState("");
  const [busy,  setBusy]    = useState(false);
  const [error, setError]   = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setError("");
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("text", text);
      const r = await fetch(`${API}/api/labs/chat`, { method: "POST", body: form });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setMessages((m) => [...m, { role: "ai", text: data.reply || "(empty reply)" }]);
    } catch (e) {
      setError(e.message || "Chat failed");
      setMessages((m) => [...m, { role: "ai", text: "⚠ Federation channel error — try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <section
      data-testid="labs-chat-console"
      className="backdrop-blur-md"
      style={{
        background:   "linear-gradient(135deg, rgba(15,23,42,0.85) 0%, rgba(10,20,40,0.85) 100%)",
        border:       "1px solid rgba(6,182,212,0.32)",
        borderRadius: 16,
        boxShadow:    "0 0 22px rgba(6,182,212,0.14)",
        padding:      18,
      }}
    >
      <div
        ref={scrollRef}
        data-testid="labs-chat-history"
        style={{
          height:        420,
          overflowY:     "auto",
          background:    "rgba(4,5,10,0.5)",
          border:        "1px solid rgba(6,182,212,0.18)",
          borderRadius:  12,
          padding:       14,
        }}
      >
        {messages.map((m, i) => <ChatBubble key={i} role={m.role} text={m.text} />)}
        {busy && <ChatBubble role="ai" text="…" />}
      </div>

      {error && (
        <div className="mt-2 font-mono text-[11px]" style={{ color: "#ff8b8b" }} data-testid="labs-chat-error">
          {error}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <input
          data-testid="labs-chat-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask the Federation…"
          disabled={busy}
          className="flex-1 font-mono"
          style={{
            padding:      "10px 14px",
            fontSize:     13,
            background:   "rgba(4,5,10,0.65)",
            border:       "1px solid rgba(6,182,212,0.32)",
            borderRadius: 8,
            color:        "#e6fbff",
            outline:      "none",
          }}
        />
        <button
          data-testid="labs-chat-send"
          type="button"
          onClick={send}
          disabled={busy || !input.trim()}
          className="font-mono uppercase tracking-[0.22em] transition-all"
          style={{
            padding:      "10px 22px",
            fontSize:     11,
            color:        "#0b0d13",
            background:   busy ? "rgba(6,182,212,0.30)" : "linear-gradient(135deg, #7fe7f5 0%, #06b6d4 100%)",
            border:       "1px solid rgba(6,182,212,0.85)",
            borderRadius: 8,
            cursor:       busy || !input.trim() ? "not-allowed" : "pointer",
            fontWeight:   700,
            boxShadow:    busy ? "none" : "0 0 18px rgba(6,182,212,0.42)",
            opacity:      !input.trim() && !busy ? 0.6 : 1,
          }}
        >
          {busy ? "…" : "Send"}
        </button>
      </div>
    </section>
  );
}

function ChatBubble({ role, text }) {
  const isUser   = role === "user";
  const isSystem = role === "system";
  return (
    <div
      data-testid={`labs-chat-bubble-${role}`}
      style={{
        display:        "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom:   10,
      }}
    >
      <div
        style={{
          maxWidth:     "78%",
          padding:      "9px 14px",
          borderRadius: isUser ? "14px 14px 3px 14px" : "14px 14px 14px 3px",
          fontSize:     13,
          lineHeight:   1.55,
          background:   isSystem
            ? "rgba(255,189,0,0.10)"
            : isUser
              ? "linear-gradient(135deg, rgba(6,182,212,0.24) 0%, rgba(6,182,212,0.14) 100%)"
              : "rgba(30,41,59,0.65)",
          border:       isSystem
            ? "1px solid rgba(255,210,74,0.35)"
            : isUser
              ? "1px solid rgba(6,182,212,0.42)"
              : "1px solid rgba(148,163,184,0.25)",
          color:        isSystem ? "#ffd24a" : "#e6fbff",
          fontFamily:   isSystem ? "'IBM Plex Mono', ui-monospace, monospace" : "inherit",
          whiteSpace:   "pre-wrap",
        }}
      >
        {text}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tool 2 · Voice Console (record / upload → transcript → reply → TTS)
// ─────────────────────────────────────────────────────────────────────
function VoiceConsole() {
  const [recording, setRecording] = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [stage,     setStage]     = useState(""); // transcribing / thinking / synthesizing
  const [transcript, setTranscript] = useState("");
  const [reply,     setReply]     = useState("");
  const [audioUrl,  setAudioUrl]  = useState("");
  const [error,     setError]     = useState("");
  const recorderRef = useRef(null);
  const chunksRef   = useRef([]);

  const startRecording = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Microphone not available in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await runPipeline(blob, "clip.webm");
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      setError(e.message || "Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await runPipeline(file, file.name || "upload.audio");
    e.target.value = "";
  };

  const runPipeline = async (blob, filename) => {
    setError(""); setTranscript(""); setReply(""); setAudioUrl("");
    setBusy(true);
    try {
      // 1. Speech → text
      setStage("transcribing");
      const sttForm = new FormData();
      sttForm.append("audio", blob, filename);
      const sttRes = await fetch(`${API}/api/labs/speech-to-text`, { method: "POST", body: sttForm });
      if (!sttRes.ok) throw new Error(`STT ${sttRes.status}`);
      const sttData = await sttRes.json();
      const t = (sttData.transcript || "").trim();
      setTranscript(t || "(silence)");
      if (!t) { setBusy(false); setStage(""); return; }

      // 2. Text → reply
      setStage("thinking");
      const chatForm = new FormData();
      chatForm.append("text", t);
      const chatRes = await fetch(`${API}/api/labs/chat`, { method: "POST", body: chatForm });
      if (!chatRes.ok) throw new Error(`Chat ${chatRes.status}`);
      const chatData = await chatRes.json();
      const r = (chatData.reply || "").trim();
      setReply(r || "(empty reply)");
      if (!r) { setBusy(false); setStage(""); return; }

      // 3. Reply → speech
      setStage("synthesizing");
      const ttsForm = new FormData();
      ttsForm.append("text", r);
      const ttsRes = await fetch(`${API}/api/labs/tts`, { method: "POST", body: ttsForm });
      if (!ttsRes.ok) throw new Error(`TTS ${ttsRes.status}`);
      const ttsData = await ttsRes.json();
      setAudioUrl(ttsData.audio_url || "");
    } catch (e) {
      setError(e.message || "Voice pipeline failed");
    } finally {
      setBusy(false);
      setStage("");
    }
  };

  return (
    <section
      data-testid="labs-voice-console"
      className="backdrop-blur-md"
      style={{
        background:   "linear-gradient(135deg, rgba(15,23,42,0.85) 0%, rgba(24,10,48,0.85) 100%)",
        border:       "1px solid rgba(216,180,254,0.32)",
        borderRadius: 16,
        boxShadow:    "0 0 22px rgba(168,85,247,0.16)",
        padding:      20,
      }}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {!recording ? (
          <button
            data-testid="labs-voice-record-start"
            type="button"
            onClick={startRecording}
            disabled={busy}
            className="font-mono uppercase tracking-[0.22em]"
            style={{
              padding:      "12px 22px",
              fontSize:     11,
              color:        busy ? "rgba(255,255,255,0.45)" : "#f3e8ff",
              background:   "rgba(4,5,10,0.65)",
              border:       "1px solid rgba(216,180,254,0.55)",
              borderRadius: 999,
              cursor:       busy ? "not-allowed" : "pointer",
              fontWeight:   700,
              boxShadow:    "0 0 14px rgba(168,85,247,0.24)",
            }}
          >
            ● Start Recording
          </button>
        ) : (
          <button
            data-testid="labs-voice-record-stop"
            type="button"
            onClick={stopRecording}
            className="font-mono uppercase tracking-[0.22em]"
            style={{
              padding:      "12px 22px",
              fontSize:     11,
              color:        "#0b0d13",
              background:   "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
              border:       "1px solid rgba(220,38,38,0.85)",
              borderRadius: 999,
              cursor:       "pointer",
              fontWeight:   700,
              animation:    "sovereign-pulse 1.6s ease-in-out infinite",
            }}
          >
            ■ Stop &amp; Send
          </button>
        )}

        <label
          data-testid="labs-voice-upload-label"
          className="font-mono uppercase tracking-[0.22em]"
          style={{
            padding:      "12px 22px",
            fontSize:     11,
            color:        "#e9d5ff",
            background:   "rgba(4,5,10,0.65)",
            border:       "1px solid rgba(216,180,254,0.32)",
            borderRadius: 999,
            cursor:       busy ? "not-allowed" : "pointer",
            opacity:      busy ? 0.5 : 1,
          }}
        >
          ↑ Upload Audio
          <input
            data-testid="labs-voice-upload-input"
            type="file"
            accept="audio/*"
            onChange={onUpload}
            disabled={busy}
            style={{ display: "none" }}
          />
        </label>

        {busy && (
          <span
            data-testid="labs-voice-stage"
            className="font-mono uppercase tracking-[0.22em]"
            style={{ fontSize: 11, color: "#d8b4fe" }}
          >
            ◉ {stage}…
          </span>
        )}
      </div>

      {error && (
        <div className="mb-3 font-mono text-[11px]" style={{ color: "#ff8b8b" }} data-testid="labs-voice-error">
          {error}
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        <ResultPanel
          testid="labs-voice-transcript"
          label="Your Voice"
          value={transcript}
          accent="#7fe7f5"
        />
        <ResultPanel
          testid="labs-voice-reply"
          label="Federation Reply"
          value={reply}
          accent="#d8b4fe"
        />
        {audioUrl && (
          <div
            data-testid="labs-voice-audio-panel"
            style={{
              background:   "rgba(4,5,10,0.6)",
              border:       "1px solid rgba(216,180,254,0.28)",
              borderRadius: 12,
              padding:      12,
            }}
          >
            <div className="font-mono uppercase tracking-[0.22em] mb-2" style={{ fontSize: 10, color: "#e9d5ff" }}>
              ▶ Federation Voice
            </div>
            <audio
              data-testid="labs-voice-audio-player"
              controls
              src={audioUrl}
              autoPlay
              style={{ width: "100%" }}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function ResultPanel({ label, value, accent, testid }) {
  if (!value) return null;
  return (
    <div
      data-testid={testid}
      style={{
        background:   "rgba(4,5,10,0.6)",
        border:       `1px solid ${accent}44`,
        borderLeft:   `3px solid ${accent}`,
        borderRadius: 10,
        padding:      "10px 14px",
      }}
    >
      <div className="font-mono uppercase tracking-[0.22em] mb-1" style={{ fontSize: 10, color: accent }}>
        {label}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.55, color: "#e6fbff", whiteSpace: "pre-wrap" }}>
        {value}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <p
      className="mt-8 font-mono text-center"
      style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "rgba(207,234,242,0.4)" }}
    >
      Powered by NextXus · DeepAI proxy · Command Labs v1
    </p>
  );
}
